/**
 * XState machine for the HLX Provider View page.
 *
 * States:
 *   $_LOADING → $_LOADED (success) | $_ERROR (failure)
 */

import { assign, fromPromise, setup } from 'xstate';
import {
  fetchProviderView,
  fetchProviderViewByResponseProvider,
  fetchProviderViewByServiceListing,
} from './api';
import type { ProviderViewResponse, ProviderViewTab } from './types';

type Context = {
  providerId: number | null;
  responseId: number | null;
  responseProviderId: number | null;
  serviceListingId: number | null;
  data: ProviderViewResponse | null;
  activeTab: ProviderViewTab;
  error: string | null;
};

type Events =
  | {
      type: 'ON_LOAD';
      payload: {
        providerId?: number;
        responseId?: number;
        responseProviderId?: number;
        serviceListingId?: number;
      };
    }
  | { type: 'ON_RETRY' }
  | { type: 'ON_TAB_CHANGE'; payload: { tab: ProviderViewTab } };

export const providerViewMachine = setup({
  types: {
    context: {} as Context,
    events: {} as Events,
    input: {} as Partial<Context>,
  },
  actors: {
    loadProviderView: fromPromise(
      async ({
        input,
      }: {
        input: {
          providerId: number | null;
          responseId: number | null;
          responseProviderId: number | null;
          serviceListingId: number | null;
        };
      }) => {
        if (input.serviceListingId != null) {
          return fetchProviderViewByServiceListing(input.serviceListingId);
        }
        if (input.responseProviderId != null) {
          return fetchProviderViewByResponseProvider(input.responseProviderId);
        }
        if (input.providerId == null) {
          throw new Error('providerId is required');
        }
        return fetchProviderView(input.providerId, input.responseId ?? undefined);
      }
    ),
  },
}).createMachine({
  id: 'providerViewState',
  context: ({ input }) => ({
    providerId: input?.providerId ?? null,
    responseId: input?.responseId ?? null,
    responseProviderId: input?.responseProviderId ?? null,
    serviceListingId: input?.serviceListingId ?? null,
    data: null,
    activeTab: 'overview' as ProviderViewTab,
    error: null,
  }),
  initial: '$_LOADING',
  on: {
    ON_TAB_CHANGE: {
      actions: assign({
        activeTab: ({ event }) => event.payload.tab,
      }),
    },
  },
  states: {
    $_LOADING: {
      invoke: {
        src: 'loadProviderView',
        input: ({ context }) => ({
          providerId: context.providerId,
          responseId: context.responseId,
          responseProviderId: context.responseProviderId,
          serviceListingId: context.serviceListingId,
        }),
        onDone: {
          target: '$_LOADED',
          actions: assign({
            data: ({ event }) => event.output,
            responseId: ({ context, event }) =>
              event.output.unmaskState.responseId ?? context.responseId ?? null,
            error: null,
          }),
        },
        onError: {
          target: '$_ERROR',
          actions: assign({
            error: ({ event }) => String(event.error),
          }),
        },
      },
    },
    $_LOADED: {
      on: {
        ON_LOAD: {
          target: '$_LOADING',
          actions: assign({
            providerId: ({ event }) => event.payload.providerId ?? null,
            responseId: ({ event }) => event.payload.responseId ?? null,
            responseProviderId: ({ event }) => event.payload.responseProviderId ?? null,
            serviceListingId: ({ event }) => event.payload.serviceListingId ?? null,
          }),
        },
      },
    },
    $_ERROR: {
      on: {
        ON_RETRY: '$_LOADING',
        ON_LOAD: {
          target: '$_LOADING',
          actions: assign({
            providerId: ({ event }) => event.payload.providerId ?? null,
            responseId: ({ event }) => event.payload.responseId ?? null,
            responseProviderId: ({ event }) => event.payload.responseProviderId ?? null,
            serviceListingId: ({ event }) => event.payload.serviceListingId ?? null,
          }),
        },
      },
    },
  },
});
